import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { CompareProvider } from "@/contexts/CompareContext";
import { ReferralCapture } from "@/components/ReferralCapture";
import { RouteChangeTracker } from "@/components/RouteChangeTracker";
import { NoIndexRoutes } from "@/components/NoIndexRoutes";
import { ScrollToTop } from "@/components/ScrollToTop";
import { CookieConsent } from "@/components/CookieConsent";

// All routes lazy-loaded including Index for smaller initial bundle
const Index = lazy(() => import("./pages/Index"));
const Collection = lazy(() => import("./pages/Collection"));
const PropertyDetail = lazy(() => import("./pages/PropertyDetail"));
const Compare = lazy(() => import("./pages/Compare"));
const ReviewComparison = lazy(() => import("./pages/ReviewComparison"));
const Reviews = lazy(() => import("./pages/Reviews"));
const Rankings = lazy(() => import("./pages/Rankings"));
const RankingsWorld = lazy(() => import("./pages/RankingsWorld"));
const RankingsEurope = lazy(() => import("./pages/RankingsEurope"));
const NewLoves = lazy(() => import("./pages/NewLoves"));
const Islands = lazy(() => import("./pages/Islands"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminBookings = lazy(() => import("./pages/AdminBookings"));
const AdminProperties = lazy(() => import("./pages/AdminProperties"));
const AdminContacts = lazy(() => import("./pages/AdminContacts"));
const AdminTags = lazy(() => import("./pages/AdminTags"));
const CustomerPortal = lazy(() => import("./pages/CustomerPortal"));
const MyBookings = lazy(() => import("./pages/MyBookings"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const AdminNewsletter = lazy(() => import("./pages/AdminNewsletter"));
const AdminReferrals = lazy(() => import("./pages/AdminReferrals"));
const AdminInsights = lazy(() => import("./pages/AdminInsights"));
const AdminBrokenLinks = lazy(() => import("./pages/AdminBrokenLinks"));
const AdminTrackingStatus = lazy(() => import("./pages/AdminTrackingStatus"));
const AdminWebVitals = lazy(() => import("./pages/AdminWebVitals"));
const AdminPinterest = lazy(() => import("./pages/AdminPinterest"));
const AdminOutboundClicks = lazy(() => import("./pages/AdminOutboundClicks"));
const AdminPriceScrapeLogs = lazy(() => import("./pages/AdminPriceScrapeLogs"));
const AdminEmails = lazy(() => import("./pages/AdminEmails"));
const AdminVerdicts = lazy(() => import("./pages/AdminVerdicts"));
const AdminStyleGuides = lazy(() => import("./pages/AdminStyleGuides"));
const AdminSeoAudit = lazy(() => import("./pages/AdminSeoAudit"));
const AdminImageQuality = lazy(() => import("./pages/AdminImageQuality"));
const BecomeAPlace = lazy(() => import("./pages/BecomeAPlace"));
const TravelerPromise = lazy(() => import("./pages/TravelerPromise"));
const Manifesto = lazy(() => import("./pages/Manifesto"));
const Methodology = lazy(() => import("./pages/Methodology"));
const Quiz = lazy(() => import("./pages/Quiz"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PinRedirect = lazy(() => import("./pages/PinRedirect"));
const Authors = lazy(() => import("./pages/Authors"));
const AuthorProfile = lazy(() => import("./pages/AuthorProfile"));
const Destinations = lazy(() => import("./pages/Destinations"));
const DestinationDetail = lazy(() => import("./pages/DestinationDetail"));
const Regions = lazy(() => import("./pages/Regions"));
const RegionDetail = lazy(() => import("./pages/RegionDetail"));
const BestOfIndex = lazy(() => import("./pages/BestOfIndex"));
const BestOfGuide = lazy(() => import("./pages/BestOfGuide"));

// Below-fold global components lazy-loaded
const ExitIntentPopup = lazy(() => import("./components/ExitIntentPopup").then(m => ({ default: m.ExitIntentPopup })));
const PropertyChatbot = lazy(() => import("./components/PropertyChatbot").then(m => ({ default: m.PropertyChatbot })));

const queryClient = new QueryClient();

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <CompareProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <RouteChangeTracker />
          <NoIndexRoutes />
          <ScrollToTop />

          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/collection" element={<Collection />} />
              <Route path="/collection/:slug" element={<PropertyDetail />} />
              <Route path="/property/:id" element={<PropertyDetail />} />
              <Route path="/compare" element={<Compare />} />
              <Route path="/rankings" element={<Rankings />} />
              <Route path="/rankings/world" element={<RankingsWorld />} />
              <Route path="/rankings/europe" element={<RankingsEurope />} />
              <Route path="/rankings/categories" element={<ReviewComparison />} />
              <Route path="/new-loves" element={<NewLoves />} />
              <Route path="/islands" element={<Islands />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/reviews/:propertyId" element={<Reviews />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/best-hotels-paris" element={<Navigate to="/blog/most-loved-hotels-in-paris" replace />} />
              <Route path="/blog/best-hotels-paris/" element={<Navigate to="/blog/most-loved-hotels-in-paris" replace />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/bookings" element={<AdminBookings />} />
              <Route path="/admin/properties" element={<AdminProperties />} />
              <Route path="/admin/contacts" element={<AdminContacts />} />
              <Route path="/admin/tags" element={<AdminTags />} />
              <Route path="/admin/blog" element={<AdminBlog />} />
              <Route path="/admin/newsletter" element={<AdminNewsletter />} />
              <Route path="/admin/referrals" element={<AdminReferrals />} />
              <Route path="/admin/insights" element={<AdminInsights />} />
              <Route path="/admin/broken-links" element={<AdminBrokenLinks />} />
              <Route path="/admin/tracking-status" element={<AdminTrackingStatus />} />
              <Route path="/admin/web-vitals" element={<AdminWebVitals />} />
              <Route path="/admin/pinterest" element={<AdminPinterest />} />
              <Route path="/admin/outbound-clicks" element={<AdminOutboundClicks />} />
              <Route path="/admin/price-scrape-logs" element={<AdminPriceScrapeLogs />} />
              <Route path="/admin/emails" element={<AdminEmails />} />
              <Route path="/admin/verdicts" element={<AdminVerdicts />} />
              <Route path="/admin/style-guides" element={<AdminStyleGuides />} />
              <Route path="/admin/seo-audit" element={<AdminSeoAudit />} />
              <Route path="/admin/image-quality" element={<AdminImageQuality />} />
              <Route path="/portal" element={<CustomerPortal />} />
              <Route path="/my-bookings" element={<MyBookings />} />
              <Route path="/about" element={<About />} />
              <Route path="/become-a-place" element={<BecomeAPlace />} />
              <Route path="/traveler-promise" element={<TravelerPromise />} />
              <Route path="/manifesto" element={<Manifesto />} />
              <Route path="/methodology" element={<Methodology />} />
              <Route path="/quiz" element={<Quiz />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/authors" element={<Authors />} />
              <Route path="/authors/:slug" element={<AuthorProfile />} />
              <Route path="/destinations" element={<Destinations />} />
              <Route path="/destinations/:slug" element={<DestinationDetail />} />
              <Route path="/regions" element={<Regions />} />
              <Route path="/regions/:slug" element={<RegionDetail />} />
              <Route path="/best" element={<BestOfIndex />} />
              <Route path="/best/:slug" element={<BestOfGuide />} />
              <Route path="/go/pin" element={<PinRedirect />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <ReferralCapture />
            <PropertyChatbot />
            <ExitIntentPopup />
            <CookieConsent />
          </Suspense>
        </BrowserRouter>
      </CompareProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
